import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Package,
  TrendingUp,
  AlertTriangle,
  Trash2,
  Zap,
  ArrowRight,
  Calendar,
  DollarSign,
  BarChart3,
  Clock,
  Loader2,
  Weight,
  Brain
} from "lucide-react";
import OnboardingWizard from "../components/OnboardingWizard";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

const CHART_COLORS = ["#0EA5E9", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6", "#EC4899"];

const PharmacyDashboard = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/pharmacy/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement du tableau de bord");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-12 h-12 text-sky-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  const overview = dashboard?.overview || {};
  const categories = dashboard?.categories || {};
  const expiringProducts = dashboard?.expiring_soon || [];
  const recentDestructions = dashboard?.recent_destructions || [];

  // Prepare chart data
  const categoryData = Object.entries(categories).map(([name, data]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value: data.count,
    amount: data.value
  }));

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CI', { 
      style: 'currency', 
      currency: 'XOF',
      maximumFractionDigits: 0 
    }).format(value);
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-8" data-testid="pharmacy-dashboard">
        {/* Onboarding Wizard */}
        <OnboardingWizard />
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-outfit font-bold text-white">
              Tableau de Bord
            </h1>
            <p className="text-slate-400 mt-1">
              Bienvenue, {user?.full_name} — {user?.pharmacy_name}
            </p>
          </div>
          
          <Link to="/destruction-express">
            <Button 
              data-testid="destruction-express-btn"
              className="btn-destruction font-bold uppercase tracking-wider gap-2"
            >
              <Zap className="w-5 h-5" />
              Destruction Express
            </Button>
          </Link>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="glass-card group">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm uppercase tracking-wider">Produits</p>
                  <p className="text-3xl font-outfit font-bold text-white mt-2 glow-text-cyan">
                    {overview.total_products || 0}
                  </p>
                </div>
                <div className="p-3 bg-sky-500/10 rounded-xl group-hover:bg-sky-500/20 transition-colors">
                  <Package className="w-6 h-6 text-sky-400" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 text-sm text-slate-500">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <span>Inventaire actif</span>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card group">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm uppercase tracking-wider">Valeur Stock</p>
                  <p className="text-3xl font-outfit font-bold text-white mt-2">
                    {formatCurrency(overview.total_value || 0)}
                  </p>
                </div>
                <div className="p-3 bg-emerald-500/10 rounded-xl group-hover:bg-emerald-500/20 transition-colors">
                  <DollarSign className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 text-sm text-slate-500">
                <BarChart3 className="w-4 h-4 text-sky-400" />
                <span>Valeur totale</span>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card group">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm uppercase tracking-wider">Destructions</p>
                  <p className="text-3xl font-outfit font-bold text-white mt-2">
                    {overview.total_destructions || 0}
                  </p>
                </div>
                <div className="p-3 bg-amber-500/100/10 rounded-xl group-hover:bg-amber-500/100/100/20 transition-colors">
                  <Trash2 className="w-6 h-6 text-amber-400" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 text-sm text-slate-500">
                <Clock className="w-4 h-4 text-amber-400" />
                <span>Total demandes</span>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card group">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-slate-400 text-sm uppercase tracking-wider">Valeur Détruite</p>
                  <p className="text-3xl font-outfit font-bold text-white mt-2 glow-text-amber">
                    {formatCurrency(overview.destruction_value || 0)}
                  </p>
                </div>
                <div className="p-3 bg-red-500/100/10 rounded-xl group-hover:bg-red-500/100/100/20 transition-colors">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 text-sm text-slate-500">
                <TrendingUp className="w-4 h-4 text-red-400" />
                <span>Montant récupérable</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Category Distribution */}
          <Card className="glass-card lg:col-span-5">
            <CardHeader>
              <CardTitle className="text-white font-outfit flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-sky-400" />
                Répartition par Catégorie
              </CardTitle>
            </CardHeader>
            <CardContent>
              {categoryData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {categoryData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                            stroke="transparent"
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          background: '#0f172a', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#e2e8f0' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-slate-500">
                  Aucune donnée disponible
                </div>
              )}
              
              {/* Legend */}
              <div className="grid grid-cols-2 gap-2 mt-4">
                {categoryData.slice(0, 6).map((cat, index) => (
                  <div key={`${cat.name}-${index}`} className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                    />
                    <span className="text-sm text-slate-400 truncate">{cat.name}</span>
                    <span className="text-sm text-white ml-auto">{cat.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Expiring Products Alert */}
          <Card className="glass-card lg:col-span-7 border-amber-500/20">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white font-outfit flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Produits Expirant Bientôt
              </CardTitle>
              <Link to="/products">
                <Button variant="ghost" size="sm" className="text-sky-400 hover:text-sky-300">
                  Voir tout <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {expiringProducts.length > 0 ? (
                <div className="space-y-3">
                  {expiringProducts.slice(0, 5).map((product) => (
                    <div 
                      key={product.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-950/50 border border-white/5"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          product.days_to_expiry < 30 ? 'bg-red-500/100' :
                          product.days_to_expiry < 60 ? 'bg-amber-500/100' : 'bg-emerald-500'
                        }`} />
                        <div>
                          <p className="text-white font-medium">{product.name}</p>
                          <p className="text-slate-500 text-sm">Lot: {product.batch_number}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge 
                          variant="outline"
                          className={`
                            ${product.days_to_expiry < 30 ? 'border-red-500/30 text-red-400' :
                              product.days_to_expiry < 60 ? 'border-amber-500/30 text-amber-400' : 
                              'border-emerald-500/30 text-emerald-400'}
                          `}
                        >
                          {product.days_to_expiry}j
                        </Badge>
                        <p className="text-slate-500 text-xs mt-1">Qté: {product.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-48 flex flex-col items-center justify-center text-slate-500">
                  <Package className="w-12 h-12 mb-3 opacity-50" />
                  <p>Aucun produit expirant prochainement</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Access - Advanced Tools */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/pharma/tonnage" data-testid="link-tonnage" className="group">
            <Card className="glass-card hover:border-sky-500/30 transition-all cursor-pointer">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="w-12 h-12 rounded-xl bg-sky-500/15 flex items-center justify-center group-hover:bg-sky-500/25 transition-colors">
                  <Weight className="w-6 h-6 text-sky-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold">Analytics Tonnage</h3>
                  <p className="text-slate-400 text-sm">Poids, attestations, carte CI, zones</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-sky-400 group-hover:translate-x-1 transition-all" />
              </CardContent>
            </Card>
          </Link>
          <Link to="/stock-intelligence" data-testid="link-stock-intelligence" className="group">
            <Card className="glass-card hover:border-amber-500/30 transition-all cursor-pointer">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center group-hover:bg-amber-500/25 transition-colors">
                  <Brain className="w-6 h-6 text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold">Alertes Stock IA</h3>
                  <p className="text-slate-400 text-sm">Peremptions, invendus, recommandations</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-amber-400 group-hover:translate-x-1 transition-all" />
              </CardContent>
            </Card>
          </Link>
          <Link to="/pharma/stock-forecast" data-testid="link-stock-forecast" className="group">
            <Card className="glass-card hover:border-emerald-500/30 transition-all cursor-pointer">
              <CardContent className="flex items-center gap-4 p-5">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/15 flex items-center justify-center group-hover:bg-emerald-500/25 transition-colors">
                  <TrendingUp className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold">Previsions de Stock</h3>
                  <p className="text-slate-400 text-sm">Previsions IA, alertes, seuils</p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Recent Destructions */}
        <Card className="glass-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white font-outfit flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-amber-400" />
              Demandes de Destruction Recentes
            </CardTitle>
            <Link to="/destructions">
              <Button variant="ghost" size="sm" className="text-sky-400 hover:text-sky-300">
                Historique <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentDestructions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="text-left border-b border-white/10">
                      <th className="pb-3 text-slate-400 font-medium text-sm">ID</th>
                      <th className="pb-3 text-slate-400 font-medium text-sm">Date</th>
                      <th className="pb-3 text-slate-400 font-medium text-sm">Produits</th>
                      <th className="pb-3 text-slate-400 font-medium text-sm">Valeur</th>
                      <th className="pb-3 text-slate-400 font-medium text-sm">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentDestructions.map((d) => (
                      <tr key={d.id} className="border-b border-white/5">
                        <td className="py-4 text-slate-300 font-mono text-sm">
                          #{d.id.slice(0, 8).toUpperCase()}
                        </td>
                        <td className="py-4 text-slate-400">
                          {new Date(d.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="py-4 text-white">
                          {d.products?.length || 0} produit(s)
                        </td>
                        <td className="py-4 text-amber-400 font-medium">
                          {formatCurrency(d.total_value)}
                        </td>
                        <td className="py-4">
                          <Badge className={`status-${d.status}`}>
                            {d.status === 'pending' ? 'En attente' :
                             d.status === 'scheduled' ? 'Planifié' :
                             d.status === 'completed' ? 'Terminé' : d.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="h-32 flex flex-col items-center justify-center text-slate-500">
                <Trash2 className="w-10 h-10 mb-2 opacity-50" />
                <p>Aucune demande de destruction</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PharmacyDashboard;
